import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ContractViewModal from "../components/ContractViewModal";
import Cookies from "js-cookie";
import "../styles/kakaoAuth.css";
import axios from "axios";
import API_ENDPOINTS from "../config/api";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 각 요소별 애니메이션 지연 시간 최적화
const staggerDelay = 0.05;

// 반응형 스타일 설정
const useResponsiveStyles = () => {
  const [isWeb, setIsWeb] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsWeb(window.innerWidth >= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const containerStyle = isWeb
    ? {
        width: "100%",
        maxWidth: "520px",
        margin: "0 auto",
      }
    : {};

  const contentStyle = isWeb
    ? {
        width: "100%",
        maxWidth: "520px",
        margin: "0 auto",
        padding: "0 20px",
      }
    : { width: "100%" };

  const summaryStyle = isWeb
    ? {
        width: "100%",
        margin: "15px 0",
        borderRadius: "12px",
      }
    : {};

  const buttonsContainerStyle = isWeb
    ? {
        width: "100%",
        maxWidth: "520px",
        margin: "20px auto",
        padding: "0 20px",
      }
    : {
        marginTop: "auto",
        marginBottom: 20,
        padding: "0 20px",
      };

  return {
    isWeb,
    containerStyle,
    contentStyle,
    summaryStyle,
    buttonsContainerStyle,
  };
};

function LawyerAgreementPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [agreementConfirmed, setAgreementConfirmed] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const {
    isWeb,
    containerStyle,
    contentStyle,
    summaryStyle,
    buttonsContainerStyle,
  } = useResponsiveStyles();

  // 모달 상태에 따른 body 스크롤 제어
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [modalOpen]);

  // useEffect(() => {
  //   // 쿠키에서 USER_INFO 가져오기
  //   const userInfoFromCookie = Cookies.get("USER_INFO");

  //   if (!userInfoFromCookie) {
  //     // USER_INFO 쿠키가 없으면 이전 페이지로 리다이렉트
  //     setErrorMessage("사용자 정보가 없습니다. 처음부터 다시 시도해주세요.");
  //     setShowErrorModal(true);
  //     setTimeout(() => {
  //       setShowErrorModal(false);
  //       navigate("/");
  //     }, 3000);
  //     return;
  //   }

  //   try {
  //     const parsedUserInfo = JSON.parse(userInfoFromCookie);
  //     setUserInfo(parsedUserInfo);
  //   } catch (error) {
  //     // 파싱 에러 발생 시 에러 메시지 표시 후 이전 페이지로 리다이렉트
  //     setErrorMessage(
  //       "사용자 정보 처리 중 오류가 발생했습니다. 다시 시도해주세요."
  //     );
  //     setShowErrorModal(true);
  //     setTimeout(() => {
  //       setShowErrorModal(false);
  //       navigate("/");
  //     }, 3000);
  //   }
  // }, [navigate]);

  const handleBack = () => {
    navigate("/bond-agreement");
  };

  const handleOpenModal = () => {
    // 모달 열기
    setModalOpen(true);
  };

  const handleNext = async () => {
    try {
      setIsLoading(true);

      // 쿠키에서 인증 코드 가져오기
      const verificationCode = Cookies.get("VERIFICATION_CODE");

      if (!verificationCode) {
        setErrorMessage("인증 정보가 없습니다. 처음부터 다시 시도해주세요.");
        setShowErrorModal(true);
        setTimeout(() => {
          setShowErrorModal(false);
          navigate("/"); // 첫 페이지로 이동
        }, 3000);
        return;
      }

      // API 호출
      const response = await axios.put(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/share/agree/${verificationCode}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // 성공 시 다음 페이지로 이동
        navigate("/signature-agreement");
      }
    } catch (error) {
      // 에러 메시지 설정 및 에러 모달 표시
      setErrorMessage(
        error.response?.data?.message ||
          "처리 중 오류가 발생했습니다. 다시 시도해주세요."
      );
      setShowErrorModal(true);

      // 3초 후에 에러 모달 닫기
      setTimeout(() => {
        setShowErrorModal(false);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalConfirm = () => {
    // 모달에서 확인 버튼 클릭 시 처리
    setAgreementConfirmed(true);
    // 모달은 onConfirm 내부에서 자동으로 닫힘
  };

  // 쿠키에서 USER_INFO 가져오기
  useEffect(() => {
    const userInfoFromCookie = Cookies.get("USER_INFO");

    if (userInfoFromCookie) {
      try {
        const parsedUserInfo = JSON.parse(userInfoFromCookie);
        setUserInfo(parsedUserInfo);
      } catch (error) {
        console.error("USER_INFO 파싱 에러:", error);
      }
    }
  }, []);

  // 사용자 정보 포맷
  const formatUserData = () => {
    if (!userInfo) return {};

    return {
      block: userInfo.claimApartmentBlock || "000",
      unit: userInfo.claimApartmentUnit || "0000",
      name: userInfo.claimName || "000",
      apartment: userInfo.apartmentName || "000아파트",
      shareName: userInfo.shareName || "000",
      claimAssignmentName: userInfo.claimAssignmentName || "000",
      claimAssignmentPhone: userInfo.claimAssignmentPhone || "000",
      claimAssignmentBirth: userInfo.claimAssignmentBirth || "000",
    };
  };
  // useEffect(() => {
  //   const verificationCode = Cookies.get("VERIFICATION_CODE");
  //   if (verificationCode === undefined) {
  //     setErrorMessage("인증 정보가 없습니다. 처음부터 다시 시도해주세요.");
  //     setShowErrorModal(true);
  //     setTimeout(() => {
  //       setShowErrorModal(false);
  //       navigate("/"); // 첫 페이지로 이동
  //     }, 2000);
  //     return;
  //   }
  // }, []);

  const userData = formatUserData();
  // userInfo가 없는 경우 로딩 상태 또는 빈 화면 표시
  // if (!userInfo) {
  //   return (
  //     <motion.div
  //       className="page-container"
  //       variants={pageVariants}
  //       initial="initial"
  //       animate="animate"
  //       exit="exit"
  //     >
  //       <motion.div
  //         className="page-header"
  //         initial={{ opacity: 0, y: 10 }}
  //         animate={{ opacity: 1, y: 0 }}
  //         transition={{ duration: 0.4, delay: staggerDelay }}
  //       >
  //         <h1>위임장 체결</h1>
  //       </motion.div>

  //       <div
  //         style={{
  //           display: "flex",
  //           justifyContent: "center",
  //           alignItems: "center",
  //           flex: 1,
  //         }}
  //       >
  //         <div className="loading-spinner"></div>
  //       </div>
  //     </motion.div>
  //   );
  // }

  return (
    <motion.div
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        ...containerStyle,
        overflow: "auto",
        height: "100vh",
      }}
    >
      {showErrorModal && (
        <motion.div
          className="error-modal"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <p>{errorMessage}</p>
        </motion.div>
      )}

      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: staggerDelay }}
      >
        <h1>공동소유자 채권양도계약 동의/승낙</h1>
      </motion.div>

      <motion.div
        className="lawyer-agreement-content"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: staggerDelay * 2 }}
        style={contentStyle}
      >
        <h2
          style={{
            fontSize: "20px",
            fontWeight: 700,
            marginBottom: "20px",
            textAlign: "left",
            marginTop: "40px",
          }}
        >
          주요 내용을 확인해주세요
        </h2>

        <motion.div
          className="agreement-summary"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: staggerDelay * 3 }}
          style={summaryStyle}
        >
          <h3>주요 내용 요약</h3>

          {[
            {
              label: "동·호수",
              value: `${userData.block}동 ${userData.unit}호`,
            },
            {
              label: "양도인(공동소유자)",
              value: `${userData.shareName}, ${userData.claimAssignmentName}`,
            },
            {
              label: "양수인",
              value: `${userData.apartment} 입주자대표회의`,
            },
            { label: "목적", value: "" },
          ].map((item, index) => (
            <motion.div
              className="summary-row"
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: staggerDelay * (4 + index),
              }}
            >
              <span className="summary-label">{item.label}</span>
              <span className="summary-value">{item.value}</span>
            </motion.div>
          ))}

          {/* 목적 상세 내용 */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              delay: staggerDelay * 8,
            }}
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E9F0",
              borderRadius: "8px",
              padding: "16px",
              marginTop: "12px",
            }}
          >
            <span
              style={{ color: "#000000", fontSize: "14px", lineHeight: "1.5" }}
            >
              공동소유자가 하자보수의무자에 대해 가지고 있는 하자보수청구권 및
              하자보수에 갈음하는 손해배상채권을 양수인에게 양도
            </span>
          </motion.div>
        </motion.div>

        {/* 버튼 영역 - 주요 내용 요약 박스 바로 밑 */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: staggerDelay * 8 }}
          style={{
            display: "flex",
            gap: "12px",
            marginTop: "30px",
            width: "100%",
          }}
        >
          <motion.button
            onClick={handleOpenModal}
            whileTap={{ scale: 0.98 }}
            disabled={isLoading}
            style={{
              flex: 1,
              height: "56px",
              backgroundColor: "#F5F7FA",
              color: "#000",
              border: "none",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTapHighlightColor: "transparent",
              outline: "none",
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = "#F5F7FA";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = "#F5F7FA";
              }
            }}
            onTouchStart={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = "#F5F7FA";
              }
            }}
            onTouchEnd={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = "#F5F7FA";
              }
            }}
          >
            계약서 전문 보기
          </motion.button>
          <motion.button
            onClick={agreementConfirmed ? handleNext : handleOpenModal}
            whileTap={{ scale: 0.98 }}
            disabled={!agreementConfirmed || isLoading}
            style={{
              flex: 1,
              height: "56px",
              backgroundColor:
                agreementConfirmed && !isLoading ? "#7B42F6" : "#EBEBFF",
              color: agreementConfirmed && !isLoading ? "white" : "#7a4fff",
              border: "none",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: "600",
              cursor:
                agreementConfirmed && !isLoading ? "pointer" : "not-allowed",
              transition: "background-color 0.2s ease",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTapHighlightColor: "transparent",
              outline: "none",
              opacity: agreementConfirmed && !isLoading ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (agreementConfirmed && !isLoading) {
                e.target.style.backgroundColor = "#6A35E5";
              }
            }}
            onMouseLeave={(e) => {
              if (agreementConfirmed && !isLoading) {
                e.target.style.backgroundColor = "#7B42F6";
              }
            }}
            onTouchStart={(e) => {
              if (agreementConfirmed && !isLoading) {
                e.target.style.backgroundColor = "#6A35E5";
              }
            }}
            onTouchEnd={(e) => {
              if (agreementConfirmed && !isLoading) {
                e.target.style.backgroundColor = "#7B42F6";
              }
            }}
          >
            {isLoading ? "처리중..." : agreementConfirmed ? "다음" : "다음"}
          </motion.button>
        </motion.div>
      </motion.div>

      {/* 계약서 전문 모달 */}
      <ContractViewModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
        userData={userData}
      />
    </motion.div>
  );
}

export default LawyerAgreementPage;
